import React, { useRef, useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import * as THREE from 'three';
import { useThreeScene } from '../hooks/useThreeScene';
import { useFreeFlightControls } from '../hooks/useFreeFlightControls';
import {
  pulseSpeedAtom, isPausedAtom, priceDirectionAtom, newBlockTriggerAtom,
  feePressureAtom, blockTimestampAtom, focusedBlockIdxAtom,
  konamiActiveAtom, konamiSloganAtom, genesisBlockAtom, pizzaDayAtom,
  lightningModeAtom, milestoneAtom,
  explorerBlockAtom, satsGameAtom, satsScoreAtom, satsPickupAtom,
  nodeMapActiveAtom, nodeCountAtom, searchedBlockAtom,
  timechainViewAtom,
} from '../state/atoms';
import { GENESIS_BLOCK } from '../utils/bitcoinData';
import { createBitcoinLogo } from './BitcoinLogo';
import { transformTesseract, generateFaces, generateCells } from '../utils/tesseract';
import { useNodeData } from '../hooks/useNodeData';
import { getCoastlineSegments } from '../utils/worldCoastline';
import { buildLandmassTexture } from '../utils/landmassTexture';

const TUBE_RADIUS = 0.022;
const VERTEX_RADIUS = 0.035;
const STAR_MAX = 8000;
const STAR_MIN = 400;
const MEMPOOL_LOW = 2000;
const MEMPOOL_HIGH = 80000;
const VIEW_DISTANCE = 3;
const SCALE = 1.2; 

const BTC_ORANGE = new THREE.Color(0xF7931A);
const PRICE_UP = new THREE.Color(0x00ff88).multiplyScalar(1.5);
const PRICE_DOWN = new THREE.Color(0xff4444).multiplyScalar(2.0);
const FLASH_WHITE = new THREE.Color(0xffffff);

const ANIM_GLOW = 0.6;
const ANIM_BREATHE = 1.0;
const ANIM_SETTLE = 1.0;
const ANIM_TOTAL = ANIM_GLOW + ANIM_BREATHE + ANIM_SETTLE;

const NET_CUBE_BASE_SIZE = 0.5;   
const NET_CUBE_MAX_SIZE = 1.2;    
const ORBIT_RADIUS_INNER = 8;     
const ORBIT_RADIUS_OUTER = 50;    
const ORBIT_SPEED = 0.012;
const SELF_ROTATE_SPEED = 0.08;
const MAX_GROUP_SIZE = Infinity;
const TX_LOW = 500;               
const TX_HIGH = 5000;

const NET_ATTACH_OFFSETS = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
  [-1, 0, 0],
  [0, -1, 0],
  [0, 0, -1],
];

const txFactor = (txCount) => {
  if (!txCount) return 0.3;
  return Math.max(0, Math.min(1, (txCount - TX_LOW) / (TX_HIGH - TX_LOW)));
};

const GLOBE_RADIUS = 2.8;

let sharedTubeGeo = null;
let sharedSphereGeo = null;

const getSharedGeo = () => {
  if (!sharedTubeGeo) {
    sharedTubeGeo = new THREE.CylinderGeometry(1, 1, 1, 6);
    sharedSphereGeo = new THREE.SphereGeometry(1, 6, 6);
  }
  return { tube: sharedTubeGeo, sphere: sharedSphereGeo };
};

const disposeSharedGeo = () => {
  if (sharedTubeGeo) { sharedTubeGeo.dispose(); sharedTubeGeo = null; }
  if (sharedSphereGeo) { sharedSphereGeo.dispose(); sharedSphereGeo = null; }
};

const createStarfield = (scene) => {
  const positions = new Float32Array(STAR_MAX * 3);
  const sizes = new Float32Array(STAR_MAX);
  const phases = new Float32Array(STAR_MAX);
  const speeds = new Float32Array(STAR_MAX);
  const BASE_SIZE = 0.08;
  for (let i = 0; i < STAR_MAX; i++) {
    const i3 = i * 3;
    const r = 15 + Math.random() * 35;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i3 + 2] = r * Math.cos(phi);
    sizes[i] = BASE_SIZE;
    phases[i] = Math.random() * Math.PI * 2;
    speeds[i] = 0.5 + Math.random() * 2.0;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
  geo.setDrawRange(0, STAR_MIN);
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      color: { value: new THREE.Color(0xffffff) },
      baseOpacity: { value: 0.7 },
    },
    vertexShader: `
      attribute float size;
      varying float vBrightness;
      void main() {
        vBrightness = size;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = 0.8 * (300.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 color;
      uniform float baseOpacity;
      varying float vBrightness;
      void main() {
        float d = length(gl_PointCoord - vec2(0.5));
        if (d > 0.5) discard;
        float core = exp(-d * d * 40.0);
        float glow = exp(-d * d * 8.0) * 0.5;
        float brightness = (core + glow) * vBrightness;
        gl_FragColor = vec4(color * brightness, baseOpacity * brightness);
      }
    `,
  });
  const stars = new THREE.Points(geo, mat);
  scene.add(stars);
  let currentCount = STAR_MIN;
  let twinkleIntensity = 0;

  const setStarCount = (txCount) => {
    if (txCount == null) return;
    const t = Math.max(0, Math.min(1, (txCount - MEMPOOL_LOW) / (MEMPOOL_HIGH - MEMPOOL_LOW)));
    const target = Math.round(STAR_MIN + t * (STAR_MAX - STAR_MIN));
    currentCount = currentCount + (target - currentCount) * 0.05;
    geo.setDrawRange(0, Math.round(currentCount));
    twinkleIntensity = twinkleIntensity + (t - twinkleIntensity) * 0.05;
  };

  const updateTwinkle = (elapsed) => {
    const count = Math.round(currentCount);
    const sizeAttr = geo.getAttribute('size');
    const amplitude = 0.15 + twinkleIntensity * 0.45;
    for (let i = 0; i < count; i++) {
      const pulse = (Math.sin(elapsed * speeds[i] + phases[i]) + 1.0) * 0.5;
      sizeAttr.array[i] = (1.0 - amplitude) + pulse * amplitude;
    }
    sizeAttr.needsUpdate = true;
  };

  return {
    stars, setStarCount, updateTwinkle,
    dispose: () => { geo.dispose(); mat.dispose(); },
  };
};

const SPARKLE_COUNT = 80;

const createSparkles = (scene) => {
  const positions = new Float32Array(SPARKLE_COUNT * 3);
  const velocities = new Float32Array(SPARKLE_COUNT * 3);
  const sizes = new Float32Array(SPARKLE_COUNT);
  const phases = new Float32Array(SPARKLE_COUNT);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      color: { value: new THREE.Color(0xF7931A) },
      opacity: { value: 0 },
    },
    vertexShader: `
      attribute float size;
      varying float vSize;
      void main() {
        vSize = size;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * (300.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 color;
      uniform float opacity;
      varying float vSize;
      void main() {
        float d = length(gl_PointCoord - vec2(0.5));
        if (d > 0.5) discard;
        float core = exp(-d * d * 50.0);
        float rays = exp(-d * d * 10.0) * 0.6;
        float sparkle = core + rays;
        gl_FragColor = vec4(color * sparkle * 5.0, opacity * sparkle);
      }
    `,
  });

  const points = new THREE.Points(geo, mat);
  points.visible = false;
  scene.add(points);

  const reset = () => {
    for (let i = 0; i < SPARKLE_COUNT; i++) {
      const i3 = i * 3;
      positions[i3] = 0;
      positions[i3 + 1] = 0;
      positions[i3 + 2] = 0;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const speed = 2.0 + Math.random() * 4.0;
      velocities[i3] = Math.sin(phi) * Math.cos(theta) * speed;
      velocities[i3 + 1] = Math.sin(phi) * Math.sin(theta) * speed;
      velocities[i3 + 2] = Math.cos(phi) * speed;
      sizes[i] = 0.4 + Math.random() * 0.6;
      phases[i] = Math.random() * Math.PI * 2;
    }
    geo.attributes.position.needsUpdate = true;
    geo.attributes.size.needsUpdate = true;
  };

  const update = (progress, elapsed) => {
    const posAttr = geo.attributes.position;
    const sizeAttr = geo.attributes.size;
    for (let i = 0; i < SPARKLE_COUNT; i++) {
      const i3 = i * 3;
      posAttr.array[i3] += velocities[i3] * 0.016;
      posAttr.array[i3 + 1] += velocities[i3 + 1] * 0.016;
      posAttr.array[i3 + 2] += velocities[i3 + 2] * 0.016;
      // Twinkle
      const twinkle = 0.7 + 0.3 * Math.sin(elapsed * 12 + phases[i]);
      sizeAttr.array[i] = sizes[i] * twinkle * (1 - progress * 0.5);
    }
    posAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;
  };

  return {
    points, mat, geo, velocities, sizes, phases,
    reset, update,
    dispose: () => { geo.dispose(); mat.dispose(); },
  };
};

const PARTICLE_COUNT = 200;

const createParticleSwirl = (scene) => {
  const positions = new Float32Array(PARTICLE_COUNT * 3);
  const baseSizes = new Float32Array(PARTICLE_COUNT);
  const sizes = new Float32Array(PARTICLE_COUNT);
  const orbitRadius = new Float32Array(PARTICLE_COUNT);
  const orbitSpeed = new Float32Array(PARTICLE_COUNT);
  const orbitPhase = new Float32Array(PARTICLE_COUNT);
  const orbitTilt = new Float32Array(PARTICLE_COUNT);
  const yOffset = new Float32Array(PARTICLE_COUNT);

  const randomize = () => {
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      orbitRadius[i] = 0.6 + Math.random() * 1.4;
      orbitSpeed[i] = (1.5 + Math.random() * 3.0) * (Math.random() > 0.5 ? 1 : -1);
      orbitPhase[i] = Math.random() * Math.PI * 2;
      orbitTilt[i] = (Math.random() - 0.5) * Math.PI * 0.8;
      yOffset[i] = (Math.random() - 0.5) * 1.6;
      baseSizes[i] = 0.2 + Math.random() * 0.35;
    }
  };
  randomize();

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      color: { value: new THREE.Color(0xF7931A) },
      opacity: { value: 0 },
    },
    vertexShader: `
      attribute float size;
      void main() {
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * (300.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 color;
      uniform float opacity;
      void main() {
        float d = length(gl_PointCoord - vec2(0.5));
        if (d > 0.5) discard;
        float glow = exp(-d * d * 18.0);
        gl_FragColor = vec4(color * glow * 4.0, opacity * glow);
      }
    `,
  });

  const points = new THREE.Points(geo, mat);
  points.visible = false;
  scene.add(points);

  const update = (elapsed, progress) => {
    const posAttr = geo.attributes.position;
    const sizeAttr = geo.attributes.size;
    const expand = 1 + progress * 1.5;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const i3 = i * 3;
      const angle = orbitPhase[i] + elapsed * orbitSpeed[i];
      const r = orbitRadius[i] * expand;
      const tilt = orbitTilt[i];
      posAttr.array[i3] = Math.cos(angle) * r;
      posAttr.array[i3 + 1] = yOffset[i] * expand + Math.sin(angle) * r * Math.sin(tilt);
      posAttr.array[i3 + 2] = Math.sin(angle) * r * Math.cos(tilt);
      sizeAttr.array[i] = baseSizes[i] * (1 + Math.sin(elapsed * 8 + orbitPhase[i]) * 0.4);
    }
    posAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;
  };

  return {
    points, mat, geo, update, randomize,
    dispose: () => { geo.dispose(); mat.dispose(); },
  };
};

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeOutExpo = (t) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);

const buildNetCube = (size, material, tubeScale = 1) => {
  const { tube, sphere } = getSharedGeo();
  const group = new THREE.Group();
  const h = size / 2;
  const corners = [
    [-h,-h,-h],[h,-h,-h],[h,-h,h],[-h,-h,h],
    [-h,h,-h],[h,h,-h],[h,h,h],[-h,h,h],
  ];
  const edges = [
    [0,1],[1,2],[2,3],[3,0],
    [4,5],[5,6],[6,7],[7,4],
    [0,4],[1,5],[2,6],[3,7],
  ];
  const r = TUBE_RADIUS * 0.8 * tubeScale;

  edges.forEach(([a, b]) => {
    const start = new THREE.Vector3(...corners[a]);
    const end = new THREE.Vector3(...corners[b]);
    const dir = new THREE.Vector3().subVectors(end, start);
    const length = dir.length();
    const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
    const mesh = new THREE.Mesh(tube, material);
    mesh.position.copy(mid);
    mesh.scale.set(r, length, r);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
    group.add(mesh);
  });

  const sr = r * 1.4;
  corners.forEach(pos => {
    const s = new THREE.Mesh(sphere, material);
    s.position.set(...pos);
    s.scale.setScalar(sr);
    group.add(s);
  });

  const hitBox = new THREE.Mesh(
    new THREE.BoxGeometry(size, size, size),
    new THREE.MeshBasicMaterial({ visible: false }),
  );
  hitBox.name = 'hitBox';
  group.add(hitBox);

  return group;
};

const INNER_CUBE_THRESHOLD = 1.5;

export const TesseractScene = ({ blockHistory = [], joystickRef, rotationJoystickRef, resetCameraRef, onHoverBlock, onInsideCubeChange, onCameraAchievement, mempoolTxCount, difficulty }) => {
  const canvasRef = useRef(null);
  const {
    sceneRef, rendererRef, cameraRef, composerRef,
    animationRef, initScene, render, updateTimer, getElapsed, getDelta, dispose,
  } = useThreeScene(canvasRef);

  const flightControls = useFreeFlightControls(canvasRef, cameraRef, {
    movementSpeed: 0.8,
    zoomSpeed: 1.0,
    initialRadius: window.innerWidth < 768 ? 10.5 : 7.0,
  });
  const flightRef = useRef(flightControls);
  flightRef.current = flightControls;

  useEffect(() => {
    if (resetCameraRef) {
      resetCameraRef.current = () => {
        flightControls.resetCamera();
        resetFacingRef.current = true;
        // Defer the atom update — callers sometimes invoke onResetCamera
        // from inside another recoil updater (e.g. keyboard handlers that
        // clear multiple atoms in one tick), which Recoil rejects. A
        // microtask sidesteps that by running after the current update
        // cycle settles.
        queueMicrotask(() => setExplorerBlockRef.current?.(null));
      };
    }
  }, [resetCameraRef, flightControls]);

  useEffect(() => {
    flightControls.onIdleResetRef.current = () => {
      resetFacingRef.current = true;
      queueMicrotask(() => setExplorerBlockRef.current?.(null));
    };
  }, [flightControls]);

  const pulseSpeed = useRecoilValue(pulseSpeedAtom);
  const isPaused = useRecoilValue(isPausedAtom);
  const priceDirection = useRecoilValue(priceDirectionAtom);
  const newBlockTrigger = useRecoilValue(newBlockTriggerAtom);
  const feePressure = useRecoilValue(feePressureAtom);
  const blockTimestamp = useRecoilValue(blockTimestampAtom);
  const focusedBlockIdx = useRecoilValue(focusedBlockIdxAtom);
  const konamiActive = useRecoilValue(konamiActiveAtom);
  const setKonamiSlogan = useSetRecoilState(konamiSloganAtom);
  const genesisActive = useRecoilValue(genesisBlockAtom);
  const searchedBlock = useRecoilValue(searchedBlockAtom);
  const pizzaDay = useRecoilValue(pizzaDayAtom);
  const lightningMode = useRecoilValue(lightningModeAtom);
  const milestoneActive = useRecoilValue(milestoneAtom);
  const setExplorerBlock = useSetRecoilState(explorerBlockAtom);
  const explorerBlock = useRecoilValue(explorerBlockAtom);
  const satsGameActive = useRecoilValue(satsGameAtom);
  const setSatsScore = useSetRecoilState(satsScoreAtom);
  const setSatsPickup = useSetRecoilState(satsPickupAtom);
  const nodeMapActive = useRecoilValue(nodeMapActiveAtom);
  const timechainView = useRecoilValue(timechainViewAtom);
  const setNodeCount = useSetRecoilState(nodeCountAtom);
  const { totalNodes } = useNodeData();

  const speedRef = useRef(pulseSpeed);
  const pausedRef = useRef(isPaused);
  const directionRef = useRef(priceDirection);
  const feePressureRef = useRef(feePressure);
  const blockTimestampRef = useRef(blockTimestamp);
  const focusedIdxRef = useRef(focusedBlockIdx);
  const currentColorRef = useRef(BTC_ORANGE.clone());
  const mempoolTxCountRef = useRef(mempoolTxCount);

  const konamiRef = useRef(false);
  const setKonamiSloganRef = useRef(setKonamiSlogan);
  const genesisRef = useRef(false);
  const searchedBlockRef = useRef(null);
  const pizzaDayRef = useRef(false);
  const lightningRef = useRef(false);
  const milestoneRef = useRef(false);
  const setExplorerBlockRef = useRef(setExplorerBlock);
  const satsGameRef = useRef(false);
  const setSatsScoreRef = useRef(setSatsScore);
  const setSatsPickupRef = useRef(setSatsPickup);
  const nodeMapRef = useRef(false);
  const globeFadeRef = useRef(0);
  const timechainRef = useRef(false);
  const chainGroupRef = useRef(null);
  const chainCubesRef = useRef([]);
  const chainRebuildRef = useRef(null);
  const chainSetVisibilityRef = useRef(null);
  const resetFacingRef = useRef(false);
  const animRef = useRef({ active: false, time: 0, triggerCount: 0 });
  const blockHistoryRef = useRef(blockHistory);
  const onHoverBlockRef = useRef(onHoverBlock);
  const onInsideCubeChangeRef = useRef(onInsideCubeChange);
  const onCameraAchievementRef = useRef(onCameraAchievement);
  const insideCubeRef = useRef(false);
  const cameraAchRef = useRef({ deepSpace: false, fullOrbit: false, baseTheta: null, totalRotation: 0, lastTheta: null });

  const morphAnglesRef = useRef({
    xw: 0, yz: 0, xy: 0,
    startXw: 0, startYz: 0, startXy: 0,
    endXw: 0, endYz: 0, endXy: 0,
  });

  const netRef = useRef({
    orbitGroups: [],
    cubeGroups: [],   
    cubeMats: [],
    animating: null,
    ejecting: null,
    initialBuilt: false,
  });

  useEffect(() => { blockHistoryRef.current = blockHistory; }, [blockHistory]);
  useEffect(() => { onHoverBlockRef.current = onHoverBlock; }, [onHoverBlock]);
  useEffect(() => { onInsideCubeChangeRef.current = onInsideCubeChange; }, [onInsideCubeChange]);
  useEffect(() => { onCameraAchievementRef.current = onCameraAchievement; }, [onCameraAchievement]);
  useEffect(() => { speedRef.current = pulseSpeed; }, [pulseSpeed]);
  useEffect(() => { pausedRef.current = isPaused; }, [isPaused]);
  useEffect(() => { directionRef.current = priceDirection; }, [priceDirection]);
  useEffect(() => { feePressureRef.current = feePressure; }, [feePressure]);
  useEffect(() => { blockTimestampRef.current = blockTimestamp; }, [blockTimestamp]);
  useEffect(() => { mempoolTxCountRef.current = mempoolTxCount; }, [mempoolTxCount]);
  useEffect(() => { konamiRef.current = konamiActive; }, [konamiActive]);
  useEffect(() => { genesisRef.current = genesisActive; }, [genesisActive]);
  useEffect(() => { searchedBlockRef.current = searchedBlock; }, [searchedBlock]);
  useEffect(() => { pizzaDayRef.current = pizzaDay; }, [pizzaDay]);
  useEffect(() => { lightningRef.current = lightningMode; }, [lightningMode]);
  useEffect(() => { milestoneRef.current = milestoneActive; }, [milestoneActive]);
  useEffect(() => {
    // When switching from globe back to tesseract, reset camera + facing
    if (!nodeMapActive && nodeMapRef.current) {
      flightControls.resetCamera();
      resetFacingRef.current = true;
    }
    nodeMapRef.current = nodeMapActive;
  }, [nodeMapActive, flightControls]);
  useEffect(() => {
    const wasOn = timechainRef.current;
    timechainRef.current = timechainView;
    if (timechainView && !wasOn) {
      chainRebuildRef.current?.(blockHistoryRef.current);
    }
    chainSetVisibilityRef.current?.(timechainView);
    if (!timechainView && wasOn) {
      // If the globe is taking over, skip the tesseract flash by jumping the
      // globe-fade nearly to its end — the next animate frame finishes the lerp
      // with tesseract opacity already at ~0.
      if (nodeMapRef.current) {
        globeFadeRef.current = 0.95;
      } else {
        flightControls.resetCamera();
        resetFacingRef.current = true;
      }
    }
  }, [timechainView, flightControls]);
  useEffect(() => {
    if (timechainRef.current) {
      chainRebuildRef.current?.(blockHistory);
    }
  }, [blockHistory]);
  useEffect(() => {
    if (!explorerBlock) return;
    const flightCtl = flightRef.current;
    const cam = cameraRef.current;
    if (!flightCtl || !cam) return;
    const allCubes = timechainRef.current ? chainCubesRef.current : netRef.current.cubeGroups;
    const cube = allCubes.find(c => c.userData.block?.height === explorerBlock.height);
    if (!cube) return;
    const worldPos = new THREE.Vector3();
    cube.getWorldPosition(worldPos);
    const cubeSize = cube.userData.cubeSize || 0.7;
    const camPos = cam.position.clone();
    const dir = camPos.clone().sub(worldPos).normalize();
    const targetCam = worldPos.clone().add(dir.multiplyScalar(cubeSize * 3));
    flightCtl.flyTo(targetCam, worldPos, 1.5);
  }, [explorerBlock, cameraRef]);
  useEffect(() => { if (totalNodes) setNodeCount(totalNodes); }, [totalNodes, setNodeCount]);
  useEffect(() => { setExplorerBlockRef.current = setExplorerBlock; }, [setExplorerBlock]);
  useEffect(() => { satsGameRef.current = satsGameActive; }, [satsGameActive]);
  useEffect(() => { setSatsScoreRef.current = setSatsScore; }, [setSatsScore]);
  useEffect(() => { setSatsPickupRef.current = setSatsPickup; }, [setSatsPickup]);

  const hadFocusRef = useRef(false);

  useEffect(() => {
    const prev = focusedIdxRef.current;
    focusedIdxRef.current = focusedBlockIdx;
    const net = netRef.current;

    if (prev === null && focusedBlockIdx === null && !hadFocusRef.current) return;
    if (focusedBlockIdx !== null) hadFocusRef.current = true;

    if (focusedBlockIdx === null) {

      netRef.current.pendingFlyHome = true;
    } else {
      const targetBlock = blockHistoryRef.current[focusedBlockIdx];
      if (!targetBlock) return;

      if (net.cubeGroups.length === 0 && blockHistoryRef.current.length > 1) {
        net.pendingBuildAll = targetBlock.height;
      } else {

        net.pendingFlyToHeight = targetBlock.height;
      }
    }
  }, [focusedBlockIdx]); 

  useEffect(() => {
    if (newBlockTrigger > 0 && newBlockTrigger !== animRef.current.triggerCount) {
      animRef.current.triggerCount = newBlockTrigger;
      // Skip visual animation if globe view is active, but still grow blocks silently
      if (nodeMapRef.current) {
        netRef.current.pendingGrow = true;
        return;
      }
      animRef.current.active = true;
      animRef.current.time = 0;

      const m = morphAnglesRef.current;
      m.startXw = m.xw;
      m.startYz = m.yz;
      m.startXy = m.xy;
      m.endXw = m.xw + Math.PI / 2;
      m.endYz = m.yz + Math.PI / 6;
      m.endXy = m.xy + Math.PI / 8;

      netRef.current.pendingGrow = true;
    }
  }, [newBlockTrigger]);

  useEffect(() => {
    if (!initScene()) return;

    const scene = sceneRef.current;

    const tesseractGroup = new THREE.Group();
    scene.add(tesseractGroup);

    const netGroup = new THREE.Group();
    scene.add(netGroup);

    const chainGroup = new THREE.Group();
    chainGroup.visible = false;
    scene.add(chainGroup);
    chainGroupRef.current = chainGroup;

    const { tube, sphere } = getSharedGeo();

    const initial = transformTesseract({ xw: 0 }, VIEW_DISTANCE);
    const edgeMeshes = [];
    const edgeMaterials = [];
    const vertexMeshes = [];

    initial.edges.forEach(([a, b]) => {
      const mat = new THREE.MeshBasicMaterial({ color: BTC_ORANGE.clone() });
      const start = new THREE.Vector3(...initial.projected[a]).multiplyScalar(SCALE);
      const end = new THREE.Vector3(...initial.projected[b]).multiplyScalar(SCALE);
      const dir = new THREE.Vector3().subVectors(end, start);
      const length = dir.length();
      const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

      const mesh = new THREE.Mesh(tube, mat);
      mesh.position.copy(mid);
      mesh.scale.set(TUBE_RADIUS, Math.max(length, 0.001), TUBE_RADIUS);
      if (length > 0.001) {
        mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
      }

      tesseractGroup.add(mesh);
      edgeMeshes.push(mesh);
      edgeMaterials.push(mat);
    });

    const vertexMaterials = [];
    initial.projected.forEach(pos => {
      const mat = new THREE.MeshBasicMaterial({ color: BTC_ORANGE.clone() });
      const mesh = new THREE.Mesh(sphere, mat);
      mesh.position.set(pos[0] * SCALE, pos[1] * SCALE, pos[2] * SCALE);
      mesh.scale.setScalar(VERTEX_RADIUS);
      tesseractGroup.add(mesh);
      vertexMeshes.push(mesh);
      vertexMaterials.push(mat);
    });

    const faces = generateFaces();
    const faceMeshes = [];
    const faceMat = new THREE.MeshBasicMaterial({
      color: 0xF7931A,
      transparent: true,
      opacity: 0.04,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    faces.forEach(([a, b, c, d]) => {
      const geo = new THREE.BufferGeometry();
      const positions = new Float32Array(18); 
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mesh = new THREE.Mesh(geo, faceMat);
      tesseractGroup.add(mesh);
      faceMeshes.push({ mesh, geo, indices: [a, b, c, d] });
    });

    const btcLogo = createBitcoinLogo();
    btcLogo.mesh.rotation.y = Math.PI * 0.28;
    tesseractGroup.add(btcLogo.mesh);

    // Ghosted reflection logos in tesseract cells (8 cells × 2 depth levels)
    const cells = generateCells();
    const CELL_DEPTH_LEVELS = 2;
    const ghostMats = Array.from({ length: CELL_DEPTH_LEVELS }, (_, depth) =>
      new THREE.MeshBasicMaterial({
        color: 0xF7931A,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      })
    );
    const cellLogos = [];
    for (let depth = 0; depth < CELL_DEPTH_LEVELS; depth++) {
      for (let c = 0; c < cells.length; c++) {
        const clone = btcLogo.mesh.clone();
        clone.traverse(child => {
          if (child.isMesh) child.material = ghostMats[depth];
        });
        clone.visible = false;
        tesseractGroup.add(clone);
        cellLogos.push({ clone, cellIdx: c, depth });
      }
    }

    const starfield = createStarfield(scene);

    scene.add(new THREE.AmbientLight(0x555555));
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.4);
    keyLight.position.set(2, 3, 4);
    scene.add(keyLight);
    const fillLight = new THREE.DirectionalLight(0xffa040, 0.5);
    fillLight.position.set(-3, 1, -2);
    scene.add(fillLight);
    const logoLight = new THREE.PointLight(0xF7931A, 0.6, 3, 2);
    logoLight.position.set(0, 0, 0.8);
    tesseractGroup.add(logoLight);

    const sparkles = createSparkles(scene);
    const particleSwirl = createParticleSwirl(scene);

    // --- GENESIS BLOCK ---
    const genesisMat = new THREE.MeshBasicMaterial({ color: 0xFFD700 });
    const genesisGroup = buildNetCube(1.0, genesisMat, 1.5);
    genesisGroup.userData.block = GENESIS_BLOCK;
    genesisGroup.userData.cubeSize = 1.0;
    const genesisPivot = new THREE.Group();
    genesisPivot.add(genesisGroup);
    genesisGroup.position.set(15, 0, 0);
    genesisPivot.visible = false;
    scene.add(genesisPivot);
    let genesisFlownTo = false;

    const genesisLabelCanvas = document.createElement('canvas');
    genesisLabelCanvas.width = 512;
    genesisLabelCanvas.height = 256;
    const genesisLabelCtx = genesisLabelCanvas.getContext('2d');
    genesisLabelCtx.font = 'bold 80px monospace';
    genesisLabelCtx.textAlign = 'center';
    genesisLabelCtx.textBaseline = 'middle';
    genesisLabelCtx.fillStyle = '#FFD700';
    genesisLabelCtx.fillText('0', 256, 128);
    const genesisLabelTex = new THREE.CanvasTexture(genesisLabelCanvas);
    genesisLabelTex.minFilter = THREE.LinearFilter;
    const genesisLabelMat = new THREE.SpriteMaterial({ map: genesisLabelTex, transparent: true, opacity: 1, depthTest: false });
    const genesisLabelSprite = new THREE.Sprite(genesisLabelMat);
    genesisLabelSprite.scale.set(1.4, 0.7, 1);
    genesisGroup.add(genesisLabelSprite);

    // --- SEARCHED BLOCK ---
    // Activated by the block search modal — red-orange to signify older / historical.
    const searchedGlow = new THREE.Color(0xFF7A1A).multiplyScalar(2.2);
    const searchedMat = new THREE.MeshBasicMaterial({ color: searchedGlow });
    const searchedGroup = buildNetCube(1.0, searchedMat, 1.5);
    searchedGroup.userData.block = null;
    searchedGroup.userData.cubeSize = 1.0;
    const searchedPivot = new THREE.Group();
    searchedPivot.add(searchedGroup);
    searchedGroup.position.set(-15, 0, 0);
    searchedPivot.visible = false;
    scene.add(searchedPivot);
    let searchedShownFor = null;
    let searchedLabelSprite = null;
    let searchedLabelMat = null;

    const setSearchedLabel = (height) => {
      if (searchedLabelSprite) {
        searchedGroup.remove(searchedLabelSprite);
        searchedLabelMat?.map?.dispose();
        searchedLabelMat?.dispose();
        searchedLabelSprite = null;
        searchedLabelMat = null;
      }
      if (height == null) return;
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, 512, 256);
      ctx.font = 'bold 80px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#FF7A1A';
      ctx.fillText(height.toLocaleString(), 256, 128);
      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;
      searchedLabelMat = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity: 1, depthTest: false });
      searchedLabelSprite = new THREE.Sprite(searchedLabelMat);
      searchedLabelSprite.scale.set(1.4, 0.7, 1);
      searchedGroup.add(searchedLabelSprite);
    };

    // --- PIZZA DAY MESHES ---
    const pizzaGroup = new THREE.Group();
    const pizzaGeos = [];
    const pizzaMats = [];

    const buildPizzaSlice = () => {
      const sliceGroup = new THREE.Group();

      // Wedge shape via ExtrudeGeometry
      const shape = new THREE.Shape();
      const angle = Math.PI * 0.3;
      shape.moveTo(0, 0);
      const segments = 12;
      for (let s = 0; s <= segments; s++) {
        const a = -angle / 2 + (angle / segments) * s;
        shape.lineTo(Math.cos(a) * 0.6, Math.sin(a) * 0.6);
      }
      shape.lineTo(0, 0);

      const extrudeSettings = { depth: 0.06, bevelEnabled: true, bevelThickness: 0.015, bevelSize: 0.015, bevelSegments: 2 };
      const wedgeGeo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
      pizzaGeos.push(wedgeGeo);

      // Cheese top
      const cheeseMat = new THREE.MeshStandardMaterial({ color: 0xFFCC00, emissive: 0xFFCC00, emissiveIntensity: 0.3, roughness: 0.6, metalness: 0.1 });
      pizzaMats.push(cheeseMat);
      const wedge = new THREE.Mesh(wedgeGeo, cheeseMat);
      wedge.rotation.x = -Math.PI / 2;
      sliceGroup.add(wedge);

      // Crust (thicker arc at the outer edge)
      const crustMat = new THREE.MeshStandardMaterial({ color: 0xC8963E, emissive: 0xC8963E, emissiveIntensity: 0.2, roughness: 0.8, metalness: 0.05 });
      pizzaMats.push(crustMat);
      const crustGeo = new THREE.TorusGeometry(0.6, 0.04, 6, 12, angle);
      pizzaGeos.push(crustGeo);
      const crust = new THREE.Mesh(crustGeo, crustMat);
      crust.rotation.x = -Math.PI / 2;
      crust.rotation.z = -angle / 2;
      crust.position.y = 0.03;
      sliceGroup.add(crust);

      // Pepperoni dots — placed on the wedge surface (XZ plane after rotation)
      const pepMat = new THREE.MeshStandardMaterial({ color: 0xCC3300, emissive: 0xCC3300, emissiveIntensity: 0.4, roughness: 0.5 });
      pizzaMats.push(pepMat);
      const pepGeo = new THREE.SphereGeometry(0.04, 8, 4);
      pepGeo.scale(1, 0.4, 1); // flatten into disc
      pizzaGeos.push(pepGeo);
      // Place pepperoni at specific angles/radii within the wedge
      const pepSpots = [
        { r: 0.2, a: 0 },
        { r: 0.35, a: 0.08 },
        { r: 0.35, a: -0.08 },
        { r: 0.48, a: 0.04 },
        { r: 0.48, a: -0.06 },
      ];
      pepSpots.forEach(({ r, a }) => {
        const pep = new THREE.Mesh(pepGeo, pepMat);
        // Wedge lies in XZ plane (after parent rotation), y is up
        pep.position.set(Math.cos(a) * r, 0.05, -Math.sin(a) * r);
        sliceGroup.add(pep);
      });

      return sliceGroup;
    };

    for (let i = 0; i < 2; i++) {
      const slice = buildPizzaSlice();
      slice.position.set(
        Math.cos(i * Math.PI) * 3, (i - 0.5) * 0.4, Math.sin(i * Math.PI) * 3,
      );
      slice.scale.setScalar(1.5);
      pizzaGroup.add(slice);
    }
    pizzaGroup.visible = false;
    scene.add(pizzaGroup);

    // --- KONAMI EXPLOSION PARTICLES ---
    const KONAMI_PARTICLE_COUNT = 2100;
    const konamiPositions = new Float32Array(KONAMI_PARTICLE_COUNT * 3);
    const konamiTargets = new Float32Array(KONAMI_PARTICLE_COUNT * 3);
    const konamiVelocities = new Float32Array(KONAMI_PARTICLE_COUNT * 3);
    const konamiSizes = new Float32Array(KONAMI_PARTICLE_COUNT);
    const konamiBaseSizes = new Float32Array(KONAMI_PARTICLE_COUNT);
    // Per-particle orbital data for nuclear motion
    const konamiOrbitRadius = new Float32Array(KONAMI_PARTICLE_COUNT);
    const konamiOrbitSpeed = new Float32Array(KONAMI_PARTICLE_COUNT);
    const konamiOrbitPhase = new Float32Array(KONAMI_PARTICLE_COUNT);
    const konamiOrbitTilt = new Float32Array(KONAMI_PARTICLE_COUNT);
    const konamiYOffset = new Float32Array(KONAMI_PARTICLE_COUNT);
    // Random intermediate shapes for contraction phase
    const KONAMI_SHAPE_COUNT = 3;
    const konamiShapes = [];
    for (let s = 0; s < KONAMI_SHAPE_COUNT; s++) {
      konamiShapes.push(new Float32Array(KONAMI_PARTICLE_COUNT * 3));
    }

    const konamiRandomizeShapes = () => {
      for (let i = 0; i < KONAMI_PARTICLE_COUNT; i++) {
        konamiOrbitRadius[i] = 0.8 + Math.random() * 2.5;
        konamiOrbitSpeed[i] = (1.0 + Math.random() * 4.0) * (Math.random() > 0.5 ? 1 : -1);
        konamiOrbitPhase[i] = Math.random() * Math.PI * 2;
        konamiOrbitTilt[i] = (Math.random() - 0.5) * Math.PI;
        konamiYOffset[i] = (Math.random() - 0.5) * 2.0;
      }

      // Generate random abstract shapes for particles to flow through
      const shapeGens = [
        // Twisted torus knot
        (i) => {
          const t = (i / KONAMI_PARTICLE_COUNT) * Math.PI * 6 + Math.random() * 0.5;
          const r = 1.5 + Math.sin(t * 3) * 0.6;
          return [
            Math.cos(t) * r + (Math.random() - 0.5) * 0.3,
            Math.sin(t * 2.5) * 0.8 + (Math.random() - 0.5) * 0.3,
            Math.sin(t) * r + (Math.random() - 0.5) * 0.3,
          ];
        },
        // Double helix / DNA
        (i) => {
          const t = (i / KONAMI_PARTICLE_COUNT) * Math.PI * 8;
          const strand = i % 2 === 0 ? 1 : -1;
          return [
            Math.cos(t) * 1.2 * strand + (Math.random() - 0.5) * 0.2,
            (i / KONAMI_PARTICLE_COUNT - 0.5) * 4 + (Math.random() - 0.5) * 0.2,
            Math.sin(t) * 1.2 * strand + (Math.random() - 0.5) * 0.2,
          ];
        },
        // Random icosahedron cloud
        (i) => {
          const phi = Math.acos(2 * Math.random() - 1);
          const theta = Math.random() * Math.PI * 2;
          const r = 1.0 + Math.random() * 1.2;
          const spikes = Math.sin(phi * 5) * Math.cos(theta * 3) * 0.8;
          return [
            Math.sin(phi) * Math.cos(theta) * (r + spikes),
            Math.cos(phi) * (r + spikes),
            Math.sin(phi) * Math.sin(theta) * (r + spikes),
          ];
        },
      ];

      // Shuffle which shape gen each shape slot gets
      const shuffled = [...shapeGens].sort(() => Math.random() - 0.5);
      for (let s = 0; s < KONAMI_SHAPE_COUNT; s++) {
        const gen = shuffled[s % shuffled.length];
        for (let i = 0; i < KONAMI_PARTICLE_COUNT; i++) {
          const i3 = i * 3;
          const [x, y, z] = gen(i);
          konamiShapes[s][i3] = x;
          konamiShapes[s][i3 + 1] = y;
          konamiShapes[s][i3 + 2] = z;
        }
      }
    };
    konamiRandomizeShapes();
    const konamiGeo = new THREE.BufferGeometry();
    konamiGeo.setAttribute('position', new THREE.BufferAttribute(konamiPositions, 3));
    konamiGeo.setAttribute('size', new THREE.BufferAttribute(konamiSizes, 1));
    const konamiMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      uniforms: {
        color: { value: new THREE.Color(0xF7931A) },
        opacity: { value: 0 },
        time: { value: 0 },
      },
      vertexShader: `
        attribute float size;
        uniform float time;
        varying float vBright;
        void main() {
          vBright = size;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (400.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float opacity;
        uniform float time;
        varying float vBright;
        void main() {
          float d = length(gl_PointCoord - vec2(0.5));
          if (d > 0.5) discard;
          float core = exp(-d * d * 30.0);
          float glow = exp(-d * d * 8.0) * 0.6;
          float sparkle = core + glow;
          vec3 c = mix(color, vec3(1.0), core * 0.5);
          gl_FragColor = vec4(c * sparkle * 5.0, opacity * sparkle * vBright);
        }
      `,
    });
    const konamiPoints = new THREE.Points(konamiGeo, konamiMat);
    konamiPoints.visible = false;
    scene.add(konamiPoints);
    let konamiAnimState = { active: false, time: 0, lastPhase: -1 };

    const KONAMI_SLOGANS = [
      'VIRES IN NUMERIS',
      "DON'T TRUST, VERIFY",
      "WE'RE ALL SATOSHI (BUT NOT CRAIG WRIGHT)",
      'RUN THE NUMBERS',
      'BE YOUR OWN BANK',
      'NOT YOUR KEYS, NOT YOUR COINS',
      'THERE IS NO SECOND BEST',
      'BITCOIN FIXES THIS',
      'STAY HUMBLE, STACK SATS',
      'THE SIGNAL IN THE NOISE',
      'TICK TOCK, NEXT BLOCK',
      'DOWN THE RABBIT HOLE',
      'FIX THE MONEY, FIX THE WORLD',
      'IN CODE WE TRUST',
      'POWERED BY PROOF OF WORK',
      'UNCONFISCATABLE',
      'INFINITY DIVIDED BY 21 MILLION',
      'HAVE FUN STAYING POOR',
      'HODL',
      'NUMBER GO UP',
      'FEW UNDERSTAND',
      'BITCOIN IS FOR ANYONE',
    ];

    // Pre-compute reform targets: distribute particles onto tesseract edges/vertices
    const konamiInitTargets = () => {
      const { projected } = transformTesseract(morphAnglesRef.current, VIEW_DISTANCE);
      for (let i = 0; i < KONAMI_PARTICLE_COUNT; i++) {
        const i3 = i * 3;
        // Spread particles along tesseract edges
        const edgeIdx = i % 32;
        const vertA = projected[Math.floor(edgeIdx / 2)];
        const vertB = projected[(Math.floor(edgeIdx / 2) + 1) % 16];
        const t = Math.random();
        konamiTargets[i3] = (vertA[0] + (vertB[0] - vertA[0]) * t) * SCALE;
        konamiTargets[i3 + 1] = (vertA[1] + (vertB[1] - vertA[1]) * t) * SCALE;
        konamiTargets[i3 + 2] = (vertA[2] + (vertB[2] - vertA[2]) * t) * SCALE;
        konamiBaseSizes[i] = 0.15 + Math.random() * 0.45;
      }
    };

    // --- LIGHTNING BOLTS ---
    const LIGHTNING_MAX_LINES = 16;
    const LIGHTNING_PASSES = 3; // multiple overlapping lines for thickness
    const lightningLines = [];
    const lightningGroup = new THREE.Group();
    const lightningMaterial = new THREE.LineBasicMaterial({
      color: 0xFFB347, transparent: true, opacity: 1.0, blending: THREE.AdditiveBlending,
    });
    const lightningGlowMat = new THREE.LineBasicMaterial({
      color: 0xF7931A, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending,
    });
    const LIGHTNING_SEGMENTS = 12;
    let lightningTimer = 0;
    let lastLightningMode = 'orbital';
    const LIGHTNING_INTERVAL = 0.08;
    for (let i = 0; i < LIGHTNING_MAX_LINES; i++) {
      const passes = [];
      for (let p = 0; p < LIGHTNING_PASSES; p++) {
        const geo = new THREE.BufferGeometry();
        const positions = new Float32Array(LIGHTNING_SEGMENTS * 3);
        geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        const line = new THREE.Line(geo, p === 0 ? lightningMaterial : lightningGlowMat);
        line.visible = false;
        lightningGroup.add(line);
        passes.push({ line, geo });
      }
      lightningLines.push(passes);
    }
    lightningGroup.visible = false;
    scene.add(lightningGroup);

    // --- MILESTONE FIREWORKS ---
    const FIREWORK_BURST_COUNT = 5;
    const FIREWORK_PARTICLES = 60;
    const fireworkBursts = [];
    const fireworkColors = [0xF7931A, 0xFFD700, 0xffffff, 0x00e5ff, 0x00ff88];
    for (let b = 0; b < FIREWORK_BURST_COUNT; b++) {
      const positions = new Float32Array(FIREWORK_PARTICLES * 3);
      const velocities = new Float32Array(FIREWORK_PARTICLES * 3);
      const sizes = new Float32Array(FIREWORK_PARTICLES);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
      const mat = new THREE.ShaderMaterial({
        transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
        uniforms: { color: { value: new THREE.Color(fireworkColors[b]) }, opacity: { value: 0 } },
        vertexShader: `
          attribute float size;
          void main() {
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            gl_PointSize = size * (300.0 / -mvPosition.z);
            gl_Position = projectionMatrix * mvPosition;
          }
        `,
        fragmentShader: `
          uniform vec3 color;
          uniform float opacity;
          void main() {
            float d = length(gl_PointCoord - vec2(0.5));
            if (d > 0.5) discard;
            float core = exp(-d * d * 40.0);
            float glow = exp(-d * d * 8.0) * 0.5;
            gl_FragColor = vec4(color * (core + glow) * 3.0, opacity * (core + glow));
          }
        `,
      });
      const pts = new THREE.Points(geo, mat);
      pts.visible = false;
      scene.add(pts);
      fireworkBursts.push({ pts, geo, mat, positions, velocities, sizes });
    }
    let fireworkState = { active: false, time: 0 };

    // --- STACK SATS PARTICLE SYSTEM ---
    const SAT_PARTICLE_COUNT = 50;
    const satPositions = new Float32Array(SAT_PARTICLE_COUNT * 3);
    const satSizes = new Float32Array(SAT_PARTICLE_COUNT);
    const satGeo = new THREE.BufferGeometry();
    satGeo.setAttribute('position', new THREE.BufferAttribute(satPositions, 3));
    satGeo.setAttribute('size', new THREE.BufferAttribute(satSizes, 1));
    const satMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      uniforms: { color: { value: new THREE.Color(0xF7931A) }, opacity: { value: 1.0 } },
      vertexShader: `
        attribute float size;
        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 color;
        uniform float opacity;
        void main() {
          float d = length(gl_PointCoord - vec2(0.5));
          if (d > 0.5) discard;
          float core = exp(-d * d * 40.0);
          float glow = exp(-d * d * 8.0) * 0.5;
          gl_FragColor = vec4(color * (core + glow) * 3.0, opacity * (core + glow));
        }
      `,
    });
    const satPoints = new THREE.Points(satGeo, satMat);
    satPoints.frustumCulled = false;
    satPoints.visible = false;
    scene.add(satPoints);
    const satParticleState = {
      positions: satPositions,
      sizes: satSizes,
      velocities: new Float32Array(SAT_PARTICLE_COUNT * 3),
      active: new Uint8Array(SAT_PARTICLE_COUNT),
      spawnTimer: 0,
    };

    // --- NODE MAP GLOBE ---
    const globeGeo = new THREE.SphereGeometry(GLOBE_RADIUS, 36, 24);
    const globeEdges = new THREE.EdgesGeometry(globeGeo);
    const globeWireMat = new THREE.LineBasicMaterial({
      color: 0xF7931A, transparent: true, opacity: 0,
    });
    const globeWireframe = new THREE.LineSegments(globeEdges, globeWireMat);
    globeWireframe.visible = false;
    scene.add(globeWireframe);

    // Continent outlines
    const coastlineVerts = getCoastlineSegments(GLOBE_RADIUS);
    const coastlineGeo = new THREE.BufferGeometry();
    coastlineGeo.setAttribute('position', new THREE.BufferAttribute(coastlineVerts, 3));
    const coastlineMat = new THREE.LineBasicMaterial({
      color: 0xF7931A, transparent: true, opacity: 0, linewidth: 1,
    });
    const coastlineLines = new THREE.LineSegments(coastlineGeo, coastlineMat);
    coastlineLines.visible = false;
    scene.add(coastlineLines);

    // Landmass texture — countries coloured by aggregated node-weight.
    // Replaces the old per-node point cloud so countries glow instead
    // of individual dots (no more nodes-in-ocean issues, reads cleanly
    // from every distance).
    const landmassTex = buildLandmassTexture();
    const landmassGeo = new THREE.SphereGeometry(GLOBE_RADIUS - 0.005, 96, 64);
    const landmassMat = new THREE.MeshBasicMaterial({
      map: landmassTex,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const landmassMesh = new THREE.Mesh(landmassGeo, landmassMat);
    landmassMesh.visible = false;
    scene.add(landmassMesh);

    const targetColor = BTC_ORANGE.clone();
    const animColor = new THREE.Color();

    const createBlockLabel = (height) => {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, 512, 256);
      ctx.font = 'bold 60px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#F7931A';
      ctx.fillText(height.toLocaleString(), 256, 128);
      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;
      const spriteMat = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        opacity: 0,
        depthTest: false,
      });
      const sprite = new THREE.Sprite(spriteMat);
      sprite.scale.set(0.9, 0.45, 1);
      return { sprite, spriteMat };
    };

    const createNetCubeMesh = (blockData, instant) => {
      const tf = txFactor(blockData?.txCount);

      const brightness = 1.4 + tf * 1.2;
      const glowColor = new THREE.Color(0xF7931A).multiplyScalar(brightness);
      const mat = new THREE.MeshBasicMaterial({ color: glowColor });

      const cubeSize = NET_CUBE_BASE_SIZE + tf * (NET_CUBE_MAX_SIZE - NET_CUBE_BASE_SIZE);
      const tubeScale = 0.8 + tf * 1.5;
      const cubeGroup = buildNetCube(cubeSize, mat, tubeScale);
      cubeGroup.scale.setScalar(instant ? 1 : 0.01);
      cubeGroup.userData.block = blockData || null;
      cubeGroup.userData.cubeSize = cubeSize;

      if (blockData?.height != null) {
        const { sprite, spriteMat } = createBlockLabel(blockData.height);
        cubeGroup.add(sprite);
        cubeGroup.userData.labelMat = spriteMat;
      }

      return { cubeGroup, mat };
    };


    const CLUSTER_TIME_THRESHOLD = 600; // 10 minutes in seconds

    const groupHasCloseTimestamp = (og, timestamp) => {
      if (timestamp == null) return false;
      for (const c of og.cubes) {
        const t = c.userData.block?.timestamp;
        if (t != null && Math.abs(t - timestamp) < CLUSTER_TIME_THRESHOLD) return true;
      }
      return false;
    };

    const growNet = (blockData, instant = false) => {
      const net = netRef.current;
      const { cubeGroup, mat } = createNetCubeMesh(blockData, instant);
      const newTimestamp = blockData?.timestamp;

      let targetGroup = null;

      // Cluster blocks mined within 10 minutes of each other
      for (const og of net.orbitGroups) {
        if (og.cubeCount < MAX_GROUP_SIZE && groupHasCloseTimestamp(og, newTimestamp)) {
          targetGroup = og;
          break;
        }
      }

      if (targetGroup) {

        const offsetIdx = targetGroup.cubeCount - 1;
        const off = NET_ATTACH_OFFSETS[offsetIdx] || NET_ATTACH_OFFSETS[0];
        const spacing = cubeGroup.userData.cubeSize || NET_CUBE_BASE_SIZE;
        cubeGroup.position.set(
          off[0] * spacing,
          off[1] * spacing,
          off[2] * spacing,
        );
        targetGroup.container.add(cubeGroup);
        targetGroup.cubeCount++;
        targetGroup.cubes.push(cubeGroup);
      } else {

        const pivot = new THREE.Group();
        const container = new THREE.Group();
        cubeGroup.position.set(0, 0, 0);
        container.add(cubeGroup);
        pivot.add(container);

        const idx = net.orbitGroups.length;
        pivot.rotation.x = (idx % 2 === 0 ? 1 : -1) * 0.12;

        netGroup.add(pivot);
        const og = { pivot, container, cubes: [cubeGroup], cubeCount: 1 };
        net.orbitGroups.push(og);
        targetGroup = og;
      }

      net.cubeGroups.push(cubeGroup);
      net.cubeMats.push(mat);
      if (!instant) {
        net.animating = { cubeGroup, time: 0 };
      }

      // Always reorder by height and redistribute after adding
      sortAndRedistributeOrbits();
    };

    const maxTimestampInGroup = (og) => {
      let max = 0;
      for (const c of og.cubes) {
        const t = c.userData.block?.timestamp;
        if (t != null && t > max) max = t;
      }
      return max;
    };

    const sortAndRedistributeOrbits = () => {
      const net = netRef.current;
      if (net.orbitGroups.length === 0) return;

      // Sort: most recent timestamp first (closest to center)
      net.orbitGroups.sort((a, b) => maxTimestampInGroup(b) - maxTimestampInGroup(a));

      const count = net.orbitGroups.length;
      const angleStep = (Math.PI * 2) / count;
      const newestTime = maxTimestampInGroup(net.orbitGroups[0]);

      net.orbitGroups.forEach((og, i) => {
        og.pivot.rotation.y = i * angleStep;

        // Radius based on time gap from newest block
        const groupTime = maxTimestampInGroup(og);
        const gap = newestTime - groupTime; // seconds
        // 0-10min → inner zone (nets), 10-20min → mid zone, 20min+ → pushed further out
        const minRadius = ORBIT_RADIUS_INNER;
        const midRadius = ORBIT_RADIUS_INNER + (ORBIT_RADIUS_OUTER - ORBIT_RADIUS_INNER) * 0.4;
        let radius;
        if (gap <= 600) {
          // Under 10 min: tight inner cluster (nets live here)
          const t = gap / 600;
          radius = minRadius + t * (midRadius - minRadius) * 0.3;
        } else if (gap <= 1200) {
          // 10-20 min: mid zone (singles)
          const t = (gap - 600) / 600;
          radius = midRadius * 0.5 + t * (midRadius - midRadius * 0.5);
        } else {
          // 20min+: pushed further out proportionally
          const t = Math.min((gap - 1200) / 1800, 1); // caps at 50min+
          radius = midRadius + t * (ORBIT_RADIUS_OUTER - midRadius);
        }
        radius *= 0.75; // tighten overall spacing (25% reduction)
        og.container.position.set(radius, 0, 0);
      });
    };

    // 1 unit per minute of block-time gap — avg 10-min spacing → ~10 unit gaps
    const TIMECHAIN_SECONDS_PER_UNIT = 60;

    const disposeChainCubes = () => {
      for (const cube of chainCubesRef.current) {
        chainGroup.remove(cube);
        cube.traverse(o => {
          if (o.geometry) o.geometry.dispose();
          if (o.material) {
            if (Array.isArray(o.material)) o.material.forEach(m => m.dispose());
            else o.material.dispose();
          }
        });
        if (cube.userData.labelMat) {
          cube.userData.labelMat.map?.dispose();
          cube.userData.labelMat.dispose();
        }
      }
      chainCubesRef.current = [];
    };

    const rebuildChain = (blocks) => {
      disposeChainCubes();
      if (!blocks || blocks.length === 0) return;
      const sorted = [...blocks].sort((a, b) => b.timestamp - a.timestamp);
      const newestTime = sorted[0].timestamp;
      for (const blockData of sorted) {
        const { cubeGroup } = createNetCubeMesh(blockData, true);
        const dt = newestTime - (blockData.timestamp || newestTime);
        cubeGroup.position.set(-dt / TIMECHAIN_SECONDS_PER_UNIT, 0, 0);
        chainGroup.add(cubeGroup);
        chainCubesRef.current.push(cubeGroup);
      }
    };

    const setChainVisibility = (active) => {
      chainGroup.visible = !!active;
      const showOrbital = !active && !nodeMapRef.current;
      tesseractGroup.visible = showOrbital;
      netRef.current.orbitGroups.forEach(og => { og.pivot.visible = showOrbital; });
      starfield.visible = showOrbital;
      if (genesisPivot) genesisPivot.visible = genesisRef.current && showOrbital;
      if (searchedPivot) searchedPivot.visible = !!searchedBlockRef.current && showOrbital;
    };

    chainRebuildRef.current = rebuildChain;
    chainSetVisibilityRef.current = setChainVisibility;

    if (timechainRef.current) {
      rebuildChain(blockHistoryRef.current);
      setChainVisibility(true);
    }

    const updateTesseract = () => {
      const { projected, edges } = transformTesseract(morphAnglesRef.current, VIEW_DISTANCE);

      for (let i = 0; i < 16; i++) {
        vertexMeshes[i].position.set(
          projected[i][0] * SCALE,
          projected[i][1] * SCALE,
          projected[i][2] * SCALE,
        );
      }

      // Update ghosted reflection logos in tesseract cells
      const camDist = cameraRef.current ? cameraRef.current.position.length() : VIEW_DISTANCE;
      const fadeStart = SCALE * 3.0;
      const fadeEnd = SCALE * 1.5;
      const baseFade = Math.max(0, Math.min(1, (fadeStart - camDist) / (fadeStart - fadeEnd)));
      const depthOpacities = [0.12, 0.04];
      for (let d = 0; d < CELL_DEPTH_LEVELS; d++) {
        ghostMats[d].opacity = baseFade * depthOpacities[d];
      }

      for (let i = 0; i < cellLogos.length; i++) {
        const { clone, cellIdx, depth } = cellLogos[i];
        if (baseFade <= 0.001) { clone.visible = false; continue; }

        const cell = cells[cellIdx];
        const verts = cell.vertexIndices;

        // Compute projected center as average of the cell's 8 vertices
        let cx = 0, cy = 0, cz = 0;
        for (let v = 0; v < verts.length; v++) {
          const pv = projected[verts[v]];
          cx += pv[0]; cy += pv[1]; cz += pv[2];
        }
        cx /= verts.length; cy /= verts.length; cz /= verts.length;

        // Cell size: average distance from center to vertices (in projected 3D)
        let avgDist = 0;
        for (let v = 0; v < verts.length; v++) {
          const pv = projected[verts[v]];
          const dx = pv[0] - cx, dy = pv[1] - cy, dz = pv[2] - cz;
          avgDist += Math.sqrt(dx * dx + dy * dy + dz * dz);
        }
        avgDist /= verts.length;

        // Depth level: shrink scale and push toward cell center (away from origin)
        const depthFactor = 1 / (depth + 1);
        const logoScale = Math.max(0.05, avgDist * SCALE * 0.3) * depthFactor;

        // For deeper levels, lerp position between origin and cell center
        const t = depth === 0 ? 1 : 0.5;
        const px = cx * t * SCALE;
        const py = cy * t * SCALE;
        const pz = cz * t * SCALE;

        // Hide if too close to origin (overlapping core logo)
        const distFromCenter = Math.sqrt(px * px + py * py + pz * pz);
        if (distFromCenter < SCALE * 0.15) { clone.visible = false; continue; }

        clone.visible = true;
        clone.position.set(px, py, pz);
        clone.scale.setScalar(logoScale);
        clone.quaternion.copy(btcLogo.mesh.quaternion);
      }

      for (let i = 0; i < edges.length; i++) {
        const [a, b] = edges[i];
        const start = new THREE.Vector3(
          projected[a][0] * SCALE, projected[a][1] * SCALE, projected[a][2] * SCALE,
        );
        const end = new THREE.Vector3(
          projected[b][0] * SCALE, projected[b][1] * SCALE, projected[b][2] * SCALE,
        );
        const dir = new THREE.Vector3().subVectors(end, start);
        const length = dir.length();
        const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

        edgeMeshes[i].position.copy(mid);
        edgeMeshes[i].scale.set(TUBE_RADIUS, Math.max(length, 0.001), TUBE_RADIUS);
        if (length > 0.001) {
          edgeMeshes[i].quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
        }
      }

      for (let i = 0; i < faceMeshes.length; i++) {
        const { geo, indices } = faceMeshes[i];
        const [a, b, c, d] = indices;
        const pa = projected[a], pb = projected[b], pc = projected[c], pd = projected[d];
        const pos = geo.attributes.position.array;

        pos[0] = pa[0]*SCALE; pos[1] = pa[1]*SCALE; pos[2] = pa[2]*SCALE;
        pos[3] = pb[0]*SCALE; pos[4] = pb[1]*SCALE; pos[5] = pb[2]*SCALE;
        pos[6] = pc[0]*SCALE; pos[7] = pc[1]*SCALE; pos[8] = pc[2]*SCALE;

        pos[9]  = pa[0]*SCALE; pos[10] = pa[1]*SCALE; pos[11] = pa[2]*SCALE;
        pos[12] = pc[0]*SCALE; pos[13] = pc[1]*SCALE; pos[14] = pc[2]*SCALE;
        pos[15] = pd[0]*SCALE; pos[16] = pd[1]*SCALE; pos[17] = pd[2]*SCALE;
        geo.attributes.position.needsUpdate = true;
      }
    };

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let lastHoveredIdx = null;

    const handleMouseMove = (e) => {
      const canvas = canvasRef.current;
      if (!canvas || !cameraRef.current) return;
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, cameraRef.current);
      const net = netRef.current;
      let hitBlock = null;
      let hitCubeGroup = null;

      const hoverCubes = timechainRef.current ? chainCubesRef.current : net.cubeGroups;
      for (let i = 0; i < hoverCubes.length; i++) {
        const hits = raycaster.intersectObjects(hoverCubes[i].children, true);
        if (hits.length > 0) {
          hitBlock = hoverCubes[i].userData.block ?? null;
          hitCubeGroup = hoverCubes[i];
          break;
        }
      }

      const hitId = hitBlock?.height ?? null;
      if (hitId !== lastHoveredIdx) {
        lastHoveredIdx = hitId;

        let screenPos = null;
        if (hitCubeGroup && cameraRef.current) {
          const worldPos = new THREE.Vector3();
          hitCubeGroup.getWorldPosition(worldPos);
          const dist = cameraRef.current.position.distanceTo(worldPos);

          // Only show tooltip when label is not visible (past FADE_FAR)
          if (dist <= 8) {
            onHoverBlockRef.current?.(null, null);
            return;
          }

          const cubeSize = hitCubeGroup.userData.cubeSize || 0.7;
          worldPos.y += cubeSize / 2 + 0.3;
          const projected = worldPos.project(cameraRef.current);
          const canvas = canvasRef.current;
          screenPos = {
            x: (projected.x * 0.5 + 0.5) * canvas.clientWidth,
            y: (-projected.y * 0.5 + 0.5) * canvas.clientHeight,
          };
        }

        onHoverBlockRef.current?.(hitBlock, screenPos);
      }
    };

    let lastPointerDown = { x: 0, y: 0 };
    const handlePointerDownForClick = (e) => {
      lastPointerDown = { x: e.clientX, y: e.clientY };
    };
    canvasRef.current?.addEventListener('pointerdown', handlePointerDownForClick);

    const handleClick = (e) => {
      const canvas = canvasRef.current;
      if (!canvas || !cameraRef.current) return;

      // Ignore clicks that were actually drags
      const dx = e.clientX - lastPointerDown.x;
      const dy = e.clientY - lastPointerDown.y;
      if (Math.sqrt(dx * dx + dy * dy) > 5) return;

      const rect = canvas.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, cameraRef.current);

      // Priority 1: Check sat particles during sats game
      if (satsGameRef.current && satPoints.visible) {
        raycaster.params.Points.threshold = 0.5;
        satPoints.geometry.computeBoundingSphere();
        const satHits = raycaster.intersectObject(satPoints);
        if (satHits.length > 0) {
          const idx = satHits[0].index;
          if (satParticleState.active[idx]) {
            satParticleState.active[idx] = 0;
            satParticleState.positions[idx * 3 + 1] = -100;
            satParticleState.sizes[idx] = 0;
            satPoints.geometry.attributes.position.needsUpdate = true;
            satPoints.geometry.attributes.size.needsUpdate = true;
            const amt = Math.floor(Math.random() * 2001) + 1000;
            setSatsScoreRef.current(s => s + amt);
            setSatsPickupRef.current(prev => [...prev, { id: Date.now() + Math.random(), amt, x: e.clientX, y: e.clientY }]);
          }
          return;
        }
      }

      // Priority 2: Check block cubes for explorer flythrough
      const net = netRef.current;
      const targetCubes = timechainRef.current ? chainCubesRef.current : net.cubeGroups;
      for (let i = 0; i < targetCubes.length; i++) {
        const hits = raycaster.intersectObjects(targetCubes[i].children, true);
        if (hits.length > 0) {
          const blockData = targetCubes[i].userData.block;
          if (blockData) {
            setExplorerBlockRef.current(blockData);
          }
          return;
        }
      }

      // Priority 3: Click on empty space — only dismiss explorer if open
      if (setExplorerBlockRef.current) {
        setExplorerBlockRef.current(null);
      }
    };

    const canvasEl = canvasRef.current;
    canvasEl?.addEventListener('mousemove', handleMouseMove);
    canvasEl?.addEventListener('click', handleClick);

    flightRef.current.initCamera();

    if (joystickRef) flightRef.current.joystickRef.current = joystickRef.current || { x: 0, y: 0 };
    if (rotationJoystickRef) flightRef.current.rotationJoystickRef.current = rotationJoystickRef.current || { x: 0, y: 0 };

    const animate = () => {
      updateTimer();
      const elapsed = getElapsed();
      const delta = getDelta();
      const anim = animRef.current;
      const net = netRef.current;

      if (joystickRef?.current) flightRef.current.joystickRef.current = joystickRef.current;
      if (rotationJoystickRef?.current) flightRef.current.rotationJoystickRef.current = rotationJoystickRef.current;

      flightRef.current.update(delta);

      // Inside-cube detection — only for the tesseract core, not individual blocks
      const orb = flightRef.current.orbitRef.current;
      const nearOrigin = orb.target.lengthSq() < 1;
      const currentlyInside = orb.radius <= INNER_CUBE_THRESHOLD && nearOrigin;
      if (currentlyInside !== insideCubeRef.current) {
        insideCubeRef.current = currentlyInside;
        onInsideCubeChangeRef.current?.(currentlyInside);
      }

      // Camera achievement detection: deep space zoom + full 360 orbit
      const camAch = cameraAchRef.current;
      if (!camAch.deepSpace && orb.radius >= 55) {
        camAch.deepSpace = true;
        onCameraAchievementRef.current?.('deepSpace');
      }
      if (!camAch.fullOrbit) {
        if (camAch.lastTheta !== null) {
          let dTheta = orb.theta - camAch.lastTheta;
          if (dTheta > Math.PI) dTheta -= Math.PI * 2;
          if (dTheta < -Math.PI) dTheta += Math.PI * 2;
          camAch.totalRotation += Math.abs(dTheta);
          if (camAch.totalRotation >= Math.PI * 2) {
            camAch.fullOrbit = true;
            onCameraAchievementRef.current?.('fullOrbit');
          }
        }
        camAch.lastTheta = orb.theta;
      }

      if (anim.active) {
        anim.time += delta;
        if (anim.time >= ANIM_TOTAL) {
          anim.active = false;
          anim.time = 0;
          anim.ejectionAngle = null;
        }
      }

      if (!net.initialBuilt && blockHistoryRef.current.length > 0) {
        net.initialBuilt = true;
        const sorted = [...blockHistoryRef.current].sort((a, b) => b.height - a.height);
        sorted.forEach(b => growNet(b, true));
      }

      if (net.pendingBuildAll != null) {
        const targetHeight = net.pendingBuildAll;
        net.pendingBuildAll = null;
        if (net.cubeGroups.length === 0 && blockHistoryRef.current.length > 0) {
          net.initialBuilt = true;
          const sorted = [...blockHistoryRef.current].sort((a, b) => b.height - a.height);
          sorted.forEach(b => growNet(b, true));
        }

        net.pendingFlyToHeight = targetHeight;
      }

      if (net.pendingFlyToHeight != null) {
        const h = net.pendingFlyToHeight;
        net.pendingFlyToHeight = null;
        sceneRef.current.updateMatrixWorld(true);
        const cubeGroup = net.cubeGroups.find(c => c.userData.block?.height === h);
        if (cubeGroup) {
          const worldPos = new THREE.Vector3();
          cubeGroup.getWorldPosition(worldPos);
          const dir = worldPos.clone().normalize();
          if (dir.lengthSq() < 0.001) dir.set(0, 0, 1);
          const camPos = worldPos.clone().add(dir.multiplyScalar(4)).setY(worldPos.y + 1.5);
          flightRef.current.flyTo(camPos, worldPos, 1.5);
        }
      }

      if (net.pendingFlyHome) {
        net.pendingFlyHome = false;
        const theta = 25 * Math.PI / 180;
        const r = 7.0;
        flightRef.current.flyTo(
          new THREE.Vector3(r * Math.sin(theta), 0, r * Math.cos(theta)),
          new THREE.Vector3(0, 0, 0),
          1.5,
        );
      }

      if (net.pendingGrow) {
        net.pendingGrow = false;
        const newBlock = blockHistoryRef.current[0];
        const alreadyExists = newBlock && net.cubeGroups.some(c => c.userData.block?.height === newBlock.height);
        if (newBlock && !alreadyExists) {
          growNet(newBlock);

          // Convert scale-in to ejection from tesseract center
          if (net.animating) {
            const { cubeGroup } = net.animating;
            const parent = cubeGroup.parent;
            const targetLocalPos = cubeGroup.position.clone();

            parent.remove(cubeGroup);
            netGroup.add(cubeGroup);
            cubeGroup.position.set(0, 0, 0);
            cubeGroup.scale.setScalar(0.01);

            // Compute ejection direction for tesseract rotation
            sceneRef.current.updateMatrixWorld(true);
            const ejWorld = targetLocalPos.clone().applyMatrix4(parent.matrixWorld);
            anim.ejectionAngle = Math.atan2(ejWorld.x, ejWorld.z);

            net.ejecting = {
              cubeGroup,
              targetParent: parent,
              targetLocalPos,
              time: 0,
              duration: 1.8,
            };
            net.animating = null;
          }
        }
      }

      if (net.ejecting) {
        const ej = net.ejecting;
        ej.time += delta;
        const p = Math.min(ej.time / ej.duration, 1);
        const ep = easeOutCubic(p);

        ej.cubeGroup.scale.setScalar(0.01 + ep * 0.99);

        sceneRef.current.updateMatrixWorld(true);
        const targetWorld = ej.targetLocalPos.clone().applyMatrix4(ej.targetParent.matrixWorld);
        ej.cubeGroup.position.lerpVectors(new THREE.Vector3(0, 0, 0), targetWorld, ep);

        if (p >= 1) {
          netGroup.remove(ej.cubeGroup);
          ej.targetParent.add(ej.cubeGroup);
          ej.cubeGroup.position.copy(ej.targetLocalPos);
          ej.cubeGroup.scale.setScalar(1);
          net.ejecting = null;
        }
      }

      if (net.animating) {
        net.animating.time += delta;
        const p = Math.min(net.animating.time / 0.5, 1);
        net.animating.cubeGroup.scale.setScalar(easeOutCubic(p));
        if (p >= 1) net.animating = null;
      }

      const t = anim.time;
      const isAnimating = anim.active;

      if (!pausedRef.current || isAnimating) {
        const speed = pausedRef.current ? 0.1 : speedRef.current;

        if (isAnimating) {
          const morphProgress = easeOutCubic(Math.min(t / ANIM_TOTAL, 1));
          const m = morphAnglesRef.current;
          m.xw = m.startXw + (m.endXw - m.startXw) * morphProgress;
          m.yz = m.startYz + (m.endYz - m.startYz) * morphProgress;
          m.xy = m.startXy + (m.endXy - m.startXy) * morphProgress;
        }

        updateTesseract();

        if (isAnimating) {
          // Rotate tesseract toward the ejection direction
          if (anim.ejectionAngle != null) {
            const diff = Math.atan2(
              Math.sin(anim.ejectionAngle - tesseractGroup.rotation.y),
              Math.cos(anim.ejectionAngle - tesseractGroup.rotation.y),
            );
            tesseractGroup.rotation.y += diff * delta * 3;
          } else {
            tesseractGroup.rotation.y += delta * 0.5 * speed;
          }
          btcLogo.mesh.rotation.y += delta * 1.32 * speed;
        } else {
          // Reset facing: smoothly rotate tesseract and logo to home orientation
          if (resetFacingRef.current) {
            const LOGO_HOME = Math.PI * 0.28;
            const lerpSpeed = Math.min(delta * 3, 1);
            // Normalize tesseractGroup.rotation.y toward 0
            const tgAngle = tesseractGroup.rotation.y % (Math.PI * 2);
            const tgDiff = Math.atan2(Math.sin(-tgAngle), Math.cos(-tgAngle));
            tesseractGroup.rotation.y += tgDiff * lerpSpeed;
            // Normalize btcLogo rotation toward home
            const logoAngle = btcLogo.mesh.rotation.y;
            const logoDiff = Math.atan2(Math.sin(LOGO_HOME - logoAngle), Math.cos(LOGO_HOME - logoAngle));
            btcLogo.mesh.rotation.y += logoDiff * lerpSpeed;
            // Done when close enough
            if (Math.abs(tgDiff) < 0.01 && Math.abs(logoDiff) < 0.01) {
              tesseractGroup.rotation.y = 0;
              btcLogo.mesh.rotation.y = LOGO_HOME;
              resetFacingRef.current = false;
            }
          } else {
            tesseractGroup.rotation.y += delta * 0.5 * speed;
            btcLogo.mesh.rotation.y += delta * 0.55 * speed;
          }
        }
        starfield.stars.rotation.y = elapsed * 0.003;
        starfield.setStarCount(mempoolTxCountRef.current);
        starfield.updateTwinkle(elapsed);

        const focusedHeight = focusedIdxRef.current !== null
          ? blockHistoryRef.current[focusedIdxRef.current]?.height
          : null;

        net.orbitGroups.forEach((og) => {
          const isFocused = focusedHeight != null &&
            og.cubes.some(c => c.userData.block?.height === focusedHeight);
          if (!isFocused) {
            og.pivot.rotation.y += delta * ORBIT_SPEED;
            og.container.rotation.y += delta * SELF_ROTATE_SPEED;
            og.container.rotation.x += delta * SELF_ROTATE_SPEED * 0.3;
          }
        });

        // Update block height label visibility based on camera distance
        const camPos = cameraRef.current.position;
        for (const cg of net.cubeGroups) {
          if (!cg.userData.labelMat) continue;
          const wp = new THREE.Vector3();
          cg.getWorldPosition(wp);
          const dist = camPos.distanceTo(wp);
          const FADE_FAR = 8;
          const FADE_NEAR = 3;
          cg.userData.labelMat.opacity = dist > FADE_FAR ? 0 : dist < FADE_NEAR ? 1 : 1 - (dist - FADE_NEAR) / (FADE_FAR - FADE_NEAR);
        }

        const setWireColor = (c) => {
          edgeMaterials.forEach(mat => mat.color.copy(c));
          vertexMaterials.forEach(mat => mat.color.copy(c));
        };

        if (isAnimating) {
          const T1 = ANIM_GLOW;
          const T2 = T1 + ANIM_BREATHE;

          if (t < T1) {

            const p = easeOutCubic(t / T1);
            animColor.lerpColors(currentColorRef.current, FLASH_WHITE, p * 0.6);
            setWireColor(animColor);

            const breathe = 1 + 0.06 * Math.sin(p * Math.PI);
            tesseractGroup.scale.setScalar(breathe);

            if (!sparkles.points.visible) {
              sparkles.reset();
              particleSwirl.randomize();
              sparkles.points.visible = true;
              particleSwirl.points.visible = true;
            }
            sparkles.mat.uniforms.opacity.value = 0.8 * (1 - p * 0.3);
            sparkles.update(p * 0.3, elapsed);
            particleSwirl.mat.uniforms.opacity.value = p * 0.7;
            particleSwirl.update(elapsed, p * 0.2);

          } else if (t < T2) {

            const p = easeOutExpo((t - T1) / ANIM_BREATHE);
            const dir = directionRef.current;
            const priceCol = dir === 'up' ? PRICE_UP : dir === 'down' ? PRICE_DOWN : BTC_ORANGE;

            animColor.lerpColors(FLASH_WHITE, priceCol, p);
            setWireColor(animColor);
            currentColorRef.current.copy(animColor);

            tesseractGroup.scale.setScalar(1);
            sparkles.mat.uniforms.opacity.value = Math.max(0, 0.6 * (1 - p));
            sparkles.update(0.3 + p * 0.5, elapsed);
            particleSwirl.mat.uniforms.opacity.value = 0.7 * (1 - p * 0.5);
            particleSwirl.update(elapsed, 0.2 + p * 0.4);

          } else {

            const p = easeOutCubic((t - T2) / ANIM_SETTLE);
            const dir = directionRef.current;
            const priceCol = dir === 'up' ? PRICE_UP : dir === 'down' ? PRICE_DOWN : BTC_ORANGE;

            currentColorRef.current.lerp(priceCol, p);
            setWireColor(currentColorRef.current);

            tesseractGroup.scale.setScalar(1);
            sparkles.mat.uniforms.opacity.value = Math.max(0, 0.3 * (1 - p));
            sparkles.update(0.8 + p * 0.2, elapsed);
            particleSwirl.mat.uniforms.opacity.value = Math.max(0, 0.35 * (1 - p));
            particleSwirl.update(elapsed, 0.6 + p * 0.4);
            if (p >= 1) {
              sparkles.points.visible = false;
              particleSwirl.points.visible = false;
            }
          }
        } else {

          tesseractGroup.scale.setScalar(1);
          sparkles.points.visible = false;
          particleSwirl.points.visible = false;

          const dir = directionRef.current;
          if (dir === 'up') targetColor.copy(PRICE_UP);
          else if (dir === 'down') targetColor.copy(PRICE_DOWN);
          else targetColor.copy(BTC_ORANGE);

          currentColorRef.current.lerp(targetColor, delta * 2);

          const fp = feePressureRef.current;
          const finalColor = currentColorRef.current.clone().lerp(FLASH_WHITE, fp * 0.25);
          setWireColor(finalColor);
        }
      }

      // --- EASTER EGG UPDATES ---

      // Genesis block visibility and fly-to
      if (genesisRef.current && !genesisPivot.visible) {
        genesisPivot.visible = true;
        if (!genesisFlownTo) {
          genesisFlownTo = true;
          const worldPos = new THREE.Vector3(15, 0, 0);
          const camPos = worldPos.clone().add(new THREE.Vector3(4, 1.5, 4));
          flightRef.current.flyTo(camPos, worldPos, 2.0);
        }
      } else if (!genesisRef.current && genesisPivot.visible) {
        genesisPivot.visible = false;
        genesisFlownTo = false;
        if (!searchedBlockRef.current) {
          const theta = 25 * Math.PI / 180;
          const r = 7.0;
          flightRef.current.flyTo(
            new THREE.Vector3(r * Math.sin(theta), 0, r * Math.cos(theta)),
            new THREE.Vector3(0, 0, 0),
            1.5,
          );
        }
      }
      if (genesisPivot.visible) {
        genesisGroup.rotation.y += delta * 0.3;
        genesisGroup.rotation.x += delta * 0.1;
        genesisMat.color.setHSL(0.12, 1, 0.5 + Math.sin(elapsed * 2) * 0.15);
      }

      // Searched block visibility and fly-to
      const currentSearched = searchedBlockRef.current;
      if (currentSearched && searchedShownFor !== currentSearched.height) {
        searchedGroup.userData.block = currentSearched;
        setSearchedLabel(currentSearched.height);
        searchedPivot.visible = true;
        searchedShownFor = currentSearched.height;
        const worldPos = new THREE.Vector3(-15, 0, 0);
        const camPos = worldPos.clone().add(new THREE.Vector3(-4, 1.5, 4));
        flightRef.current.flyTo(camPos, worldPos, 2.0);
      } else if (!currentSearched && searchedShownFor !== null) {
        searchedPivot.visible = false;
        searchedGroup.userData.block = null;
        setSearchedLabel(null);
        searchedShownFor = null;
        if (!genesisRef.current) {
          const theta = 25 * Math.PI / 180;
          const r = 7.0;
          flightRef.current.flyTo(
            new THREE.Vector3(r * Math.sin(theta), 0, r * Math.cos(theta)),
            new THREE.Vector3(0, 0, 0),
            1.5,
          );
        }
      }
      if (searchedPivot.visible) {
        searchedGroup.rotation.y += delta * 0.3;
        searchedGroup.rotation.x += delta * 0.1;
        if (searchedLabelMat) {
          const wp = new THREE.Vector3();
          searchedGroup.getWorldPosition(wp);
          const dist = cameraRef.current.position.distanceTo(wp);
          const FADE_FAR = 10;
          const FADE_NEAR = 3;
          searchedLabelMat.opacity = dist > FADE_FAR ? 0 : dist < FADE_NEAR ? 1 : 1 - (dist - FADE_NEAR) / (FADE_FAR - FADE_NEAR);
        }
      }
      if (genesisPivot.visible && genesisLabelMat) {
        const wp = new THREE.Vector3();
        genesisGroup.getWorldPosition(wp);
        const dist = cameraRef.current.position.distanceTo(wp);
        const FADE_FAR = 10;
        const FADE_NEAR = 3;
        genesisLabelMat.opacity = dist > FADE_FAR ? 0 : dist < FADE_NEAR ? 1 : 1 - (dist - FADE_NEAR) / (FADE_FAR - FADE_NEAR);
      }

      // Pizza day meshes
      if (pizzaDayRef.current && !pizzaGroup.visible) {
        pizzaGroup.visible = true;
      } else if (!pizzaDayRef.current && pizzaGroup.visible) {
        pizzaGroup.visible = false;
      }
      if (pizzaGroup.visible) {
        pizzaGroup.rotation.y += delta * 0.5;
        pizzaGroup.children.forEach((slice, i) => {
          slice.position.y = Math.sin(elapsed * 2 + i * Math.PI) * 0.3 + (i - 0.5) * 0.4;
          slice.rotation.y += delta * 0.8;
          slice.rotation.x = Math.sin(elapsed * 1.5 + i * 2) * 0.2;
        });
      }

      // Konami explosion — center camera, then: explode, swirl, reform, crossfade
      if (konamiRef.current && !konamiAnimState.active) {
        konamiRef.current = false;
        konamiAnimState = { active: true, time: -1.5, lastPhase: -1 };
        konamiPoints.visible = false;
        setKonamiSloganRef.current(KONAMI_SLOGANS[Math.floor(Math.random() * KONAMI_SLOGANS.length)]);
        // Fly camera home first
        flightRef.current.resetCamera();
      }
      if (konamiAnimState.active) {
        konamiAnimState.time += delta;
        const kt = konamiAnimState.time;

        if (kt < 0) {
          // Centering phase — camera is flying home, do nothing
        } else {
          // Initialize particles on first frame after centering
          if (!konamiPoints.visible) {
            konamiPoints.visible = true;
            konamiInitTargets();
            konamiRandomizeShapes();
            for (let i = 0; i < KONAMI_PARTICLE_COUNT; i++) {
              const i3 = i * 3;
              konamiPositions[i3] = konamiTargets[i3];
              konamiPositions[i3 + 1] = konamiTargets[i3 + 1];
              konamiPositions[i3 + 2] = konamiTargets[i3 + 2];
              const th = Math.random() * Math.PI * 2;
              const ph = Math.acos(2 * Math.random() - 1);
              const speed = 4 + Math.random() * 10;
              konamiVelocities[i3] = Math.sin(ph) * Math.cos(th) * speed;
              konamiVelocities[i3 + 1] = Math.sin(ph) * Math.sin(th) * speed;
              konamiVelocities[i3 + 2] = Math.cos(ph) * speed;
              konamiSizes[i] = konamiBaseSizes[i];
            }
            konamiGeo.attributes.position.needsUpdate = true;
            konamiGeo.attributes.size.needsUpdate = true;
            tesseractGroup.visible = false;
          }

          const posAttr = konamiGeo.attributes.position;
          const sizeAttr = konamiGeo.attributes.size;
          konamiMat.uniforms.time.value = kt;

          // Timeline: 0-1.5s explode, 1.5-2.7 shape1, 2.7-3.9 shape2, 3.9-5.1 shape3, 5.1-6.6 tesseract reform, 6.6-8.5 crossfade
          // All phases have continuous orbital motion layered on top

          // Clear slogan during crossfade
          if (kt >= 6.6 && konamiAnimState.lastPhase !== 5) {
            konamiAnimState.lastPhase = 5;
            setKonamiSloganRef.current(null);
          }

          // Orbital expansion: burst out then gradually contract
          let orbitExpand;
          if (kt < 1.5) {
            orbitExpand = 1 + easeOutCubic(kt / 1.5) * 6;
          } else if (kt < 2.7) {
            orbitExpand = 7 - easeOutCubic((kt - 1.5) / 1.2) * 3;
          } else {
            orbitExpand = Math.max(0.3, 4 - (kt - 2.7) * 0.8);
          }

          // Determine which shape we're morphing toward
          const S = 1.2; // shape phase duration
          let shapeFrom, shapeTo, morphP;

          if (kt < 1.5) {
            shapeFrom = null; shapeTo = null; morphP = 0;
          } else if (kt < 1.5 + S) {
            shapeFrom = null; shapeTo = konamiShapes[0]; morphP = easeOutCubic((kt - 1.5) / S);
          } else if (kt < 1.5 + S * 2) {
            shapeFrom = konamiShapes[0]; shapeTo = konamiShapes[1]; morphP = easeOutCubic((kt - 1.5 - S) / S);
          } else if (kt < 1.5 + S * 3) {
            shapeFrom = konamiShapes[1]; shapeTo = konamiShapes[2]; morphP = easeOutCubic((kt - 1.5 - S * 2) / S);
          } else if (kt < 1.5 + S * 3 + 1.5) {
            shapeFrom = konamiShapes[2]; shapeTo = konamiTargets; morphP = easeOutCubic((kt - 1.5 - S * 3) / 1.5);
          } else {
            shapeFrom = null; shapeTo = konamiTargets; morphP = 1;
          }

          konamiMat.uniforms.opacity.value = Math.min(1, kt * 3);

          // Flash white at start, settle to orange
          const flashP = Math.max(0, 1 - kt * 1.5);
          konamiMat.uniforms.color.value.setRGB(
            0.97 + flashP * 0.03, 0.57 + flashP * 0.43, 0.10 + flashP * 0.90,
          );

          if (kt < 6.6) {
            // Orbital motion + shape morphing
            for (let i = 0; i < KONAMI_PARTICLE_COUNT; i++) {
              const i3 = i * 3;

              // Base orbital position (always moving)
              const angle = konamiOrbitPhase[i] + elapsed * konamiOrbitSpeed[i];
              const r = konamiOrbitRadius[i] * orbitExpand;
              const tilt = konamiOrbitTilt[i];
              const ox = Math.cos(angle) * r;
              const oy = konamiYOffset[i] * orbitExpand + Math.sin(angle) * r * Math.sin(tilt);
              const oz = Math.sin(angle) * r * Math.cos(tilt);

              if (!shapeTo) {
                // Pure orbital explosion
                posAttr.array[i3] = ox;
                posAttr.array[i3 + 1] = oy;
                posAttr.array[i3 + 2] = oz;
              } else {
                // Blend: orbital jitter fades as morphP increases
                const jitter = Math.max(0, 1 - morphP * 1.2);
                let tx, ty, tz;
                if (shapeFrom) {
                  tx = shapeFrom[i3] + (shapeTo[i3] - shapeFrom[i3]) * morphP;
                  ty = shapeFrom[i3 + 1] + (shapeTo[i3 + 1] - shapeFrom[i3 + 1]) * morphP;
                  tz = shapeFrom[i3 + 2] + (shapeTo[i3 + 2] - shapeFrom[i3 + 2]) * morphP;
                } else {
                  tx = shapeTo[i3];
                  ty = shapeTo[i3 + 1];
                  tz = shapeTo[i3 + 2];
                }
                posAttr.array[i3] = tx + ox * jitter * 0.3;
                posAttr.array[i3 + 1] = ty + oy * jitter * 0.3;
                posAttr.array[i3 + 2] = tz + oz * jitter * 0.3;
              }

              sizeAttr.array[i] = konamiBaseSizes[i] * (1 + Math.sin(elapsed * 8 + konamiOrbitPhase[i]) * 0.4);
            }
          } else if (kt < 8.5) {
            // Crossfade — particles fade out, tesseract fades in
            const p = (kt - 6.6) / 1.9;
            const fadeOut = 1 - easeOutCubic(p);
            konamiMat.uniforms.opacity.value = fadeOut;
            if (!tesseractGroup.visible) {
              tesseractGroup.visible = true;
              edgeMaterials.forEach(mat => { mat.transparent = true; mat.opacity = 0; });
              vertexMaterials.forEach(mat => { mat.transparent = true; mat.opacity = 0; });
            }
            const fadeIn = easeOutCubic(p);
            edgeMaterials.forEach(mat => { mat.opacity = fadeIn; });
            vertexMaterials.forEach(mat => { mat.opacity = fadeIn; });
            for (let i = 0; i < KONAMI_PARTICLE_COUNT; i++) {
              const i3 = i * 3;
              posAttr.array[i3] = konamiTargets[i3];
              posAttr.array[i3 + 1] = konamiTargets[i3 + 1];
              posAttr.array[i3 + 2] = konamiTargets[i3 + 2];
              sizeAttr.array[i] = konamiBaseSizes[i] * fadeOut;
            }
          } else {
            // Done — clean up
            konamiAnimState.active = false;
            konamiPoints.visible = false;
            tesseractGroup.visible = true;
            konamiMat.uniforms.opacity.value = 0;
            konamiMat.uniforms.color.value.setHex(0xF7931A);
            edgeMaterials.forEach(mat => { mat.transparent = false; mat.opacity = 1; });
            vertexMaterials.forEach(mat => { mat.transparent = false; mat.opacity = 1; });
          }
          posAttr.needsUpdate = true;
          sizeAttr.needsUpdate = true;
        }
      }

      // Lightning mode — suppressed while the Node Globe is on screen so
      // the two visuals don't fight. When the user exits the globe,
      // lightning resumes automatically (the atom state is untouched).
      if (lightningRef.current && !nodeMapRef.current) {
        lightningGroup.visible = true;
        lightningTimer += delta;
        let shouldRegen = lightningTimer >= LIGHTNING_INTERVAL;
        const currentLightningMode = timechainRef.current ? 'chain' : 'orbital';
        if (currentLightningMode !== lastLightningMode) {
          shouldRegen = true;
          lastLightningMode = currentLightningMode;
        }
        if (shouldRegen) lightningTimer = 0;

        sceneRef.current.updateMatrixWorld(true);
        const cubeSource = timechainRef.current ? chainCubesRef.current : netRef.current.cubeGroups;
        const cubes = [...cubeSource].sort((a, b) =>
          (a.userData.block?.height || 0) - (b.userData.block?.height || 0)
        );
        for (let i = 0; i < LIGHTNING_MAX_LINES; i++) {
          const passes = lightningLines[i];
          if (i >= cubes.length - 1) {
            passes.forEach(p => { p.line.visible = false; });
            continue;
          }

          if (shouldRegen) {
            const posA = new THREE.Vector3();
            const posB = new THREE.Vector3();
            cubes[i].getWorldPosition(posA);
            cubes[i + 1].getWorldPosition(posB);

            for (let p = 0; p < LIGHTNING_PASSES; p++) {
              passes[p].line.visible = true;
              const posArr = passes[p].geo.attributes.position.array;
              const jitterScale = p === 0 ? 0.8 : 0.8 + p * 0.35;
              for (let s = 0; s < LIGHTNING_SEGMENTS; s++) {
                const t = s / (LIGHTNING_SEGMENTS - 1);
                const pt = posA.clone().lerp(posB, t);
                if (s > 0 && s < LIGHTNING_SEGMENTS - 1) {
                  pt.x += (Math.random() - 0.5) * jitterScale;
                  pt.y += (Math.random() - 0.5) * jitterScale;
                  pt.z += (Math.random() - 0.5) * jitterScale;
                }
                posArr[s * 3] = pt.x;
                posArr[s * 3 + 1] = pt.y;
                posArr[s * 3 + 2] = pt.z;
              }
              passes[p].geo.attributes.position.needsUpdate = true;
              passes[p].geo.setDrawRange(0, LIGHTNING_SEGMENTS);
            }
          }
        }
        if (shouldRegen) {
          lightningMaterial.opacity = 0.7 + Math.random() * 0.3;
          lightningGlowMat.opacity = 0.3 + Math.random() * 0.4;
        }
      } else {
        lightningGroup.visible = false;
        lightningTimer = 0;
      }

      // Milestone fireworks
      if (milestoneRef.current && !fireworkState.active) {
        fireworkState = { active: true, time: 0 };
        fireworkBursts.forEach((burst, b) => {
          const delay = b * 0.4;
          const center = new THREE.Vector3(
            (Math.random() - 0.5) * 4, (Math.random() - 0.5) * 3, (Math.random() - 0.5) * 4,
          );
          for (let i = 0; i < FIREWORK_PARTICLES; i++) {
            const i3 = i * 3;
            burst.positions[i3] = center.x;
            burst.positions[i3 + 1] = center.y;
            burst.positions[i3 + 2] = center.z;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos(2 * Math.random() - 1);
            const speed = 2 + Math.random() * 5;
            burst.velocities[i3] = Math.sin(phi) * Math.cos(theta) * speed;
            burst.velocities[i3 + 1] = Math.sin(phi) * Math.sin(theta) * speed;
            burst.velocities[i3 + 2] = Math.cos(phi) * speed;
            burst.sizes[i] = 0.3 + Math.random() * 0.4;
          }
          burst.geo.attributes.position.needsUpdate = true;
          burst.geo.attributes.size.needsUpdate = true;
          burst.pts.visible = true;
          burst.mat.uniforms.opacity.value = 0;
          burst._delay = delay;
        });
      }
      if (fireworkState.active) {
        fireworkState.time += delta;
        let allDone = true;
        fireworkBursts.forEach((burst) => {
          const bt = fireworkState.time - (burst._delay || 0);
          if (bt < 0) { allDone = false; return; }
          if (bt > 3.0) {
            burst.pts.visible = false;
            return;
          }
          allDone = false;
          burst.mat.uniforms.opacity.value = bt < 0.3 ? bt / 0.3 : Math.max(0, 1 - (bt - 0.3) / 2.7);
          const posAttr = burst.geo.attributes.position;
          for (let i = 0; i < FIREWORK_PARTICLES; i++) {
            const i3 = i * 3;
            posAttr.array[i3] += burst.velocities[i3] * delta;
            posAttr.array[i3 + 1] += burst.velocities[i3 + 1] * delta;
            posAttr.array[i3 + 2] += burst.velocities[i3 + 2] * delta;
          }
          posAttr.needsUpdate = true;
        });
        if (allDone) fireworkState.active = false;
      }

      // --- STACK SATS GAME ---
      if (satsGameRef.current) {
        if (!satPoints.visible) {
          satPoints.visible = true;
          satParticleState.spawnTimer = 0;
          for (let i = 0; i < SAT_PARTICLE_COUNT; i++) {
            satParticleState.active[i] = 0;
            satParticleState.sizes[i] = 0;
            satParticleState.positions[i * 3] = 0;
            satParticleState.positions[i * 3 + 1] = 0;
            satParticleState.positions[i * 3 + 2] = 0;
          }
        }
        // Spawn sats from tesseract center, exploding toward the camera
        const cam = cameraRef.current;
        const camPos = cam ? cam.position : new THREE.Vector3(0, 0, 7);
        const camDist = camPos.length();

        satParticleState.spawnTimer += delta;
        if (satParticleState.spawnTimer > 0.25) {
          satParticleState.spawnTimer = 0;
          let spawned = 0;
          for (let i = 0; i < SAT_PARTICLE_COUNT && spawned < 2; i++) {
            if (!satParticleState.active[i]) {
              satParticleState.active[i] = 1;
              // Spawn near origin with slight random offset
              satParticleState.positions[i * 3] = (Math.random() - 0.5) * 0.5;
              satParticleState.positions[i * 3 + 1] = (Math.random() - 0.5) * 0.5;
              satParticleState.positions[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
              // Velocity: toward camera with spread
              const toCamera = new THREE.Vector3()
                .copy(camPos).normalize();
              const spread = new THREE.Vector3(
                (Math.random() - 0.5) * 0.6,
                (Math.random() - 0.5) * 0.6,
                (Math.random() - 0.5) * 0.6,
              );
              toCamera.add(spread).normalize();
              const speed = 0.8 + Math.random() * 0.6;
              satParticleState.velocities[i * 3] = toCamera.x * speed;
              satParticleState.velocities[i * 3 + 1] = toCamera.y * speed;
              satParticleState.velocities[i * 3 + 2] = toCamera.z * speed;
              satParticleState.sizes[i] = 0.4 + Math.random() * 0.3;
              spawned++;
            }
          }
        }
        for (let i = 0; i < SAT_PARTICLE_COUNT; i++) {
          if (satParticleState.active[i]) {
            satParticleState.positions[i * 3] += satParticleState.velocities[i * 3] * delta;
            satParticleState.positions[i * 3 + 1] += satParticleState.velocities[i * 3 + 1] * delta;
            satParticleState.positions[i * 3 + 2] += satParticleState.velocities[i * 3 + 2] * delta;
            // Kill when past the camera
            const px = satParticleState.positions[i * 3];
            const py = satParticleState.positions[i * 3 + 1];
            const pz = satParticleState.positions[i * 3 + 2];
            const dist = Math.sqrt(px * px + py * py + pz * pz);
            if (dist > camDist + 2) {
              satParticleState.active[i] = 0;
              satParticleState.sizes[i] = 0;
            }
          }
        }
        satPoints.geometry.attributes.position.needsUpdate = true;
        satPoints.geometry.attributes.size.needsUpdate = true;
      } else if (satPoints.visible) {
        satPoints.visible = false;
      }

      // --- NODE MAP GLOBE ---
      const globeTarget = nodeMapRef.current ? 1 : 0;
      const globeFade = globeFadeRef.current;
      if (Math.abs(globeFade - globeTarget) > 0.001) {
        globeFadeRef.current += (globeTarget - globeFade) * Math.min(delta * 8, 1);
        const f = globeFadeRef.current;

        // Fade globe in/out
        globeWireframe.visible = f > 0.01;
        landmassMesh.visible = f > 0.01;
        coastlineLines.visible = f > 0.01;
        globeWireMat.opacity = f * 0.15;
        coastlineMat.opacity = f * 0.75;
        landmassMat.opacity = f * 0.95;

        // Fade tesseract + blocks out/in (cube fades fast with power curve)
        const tFade = Math.pow(1 - f, 2.5);
        const hiding = tFade < 0.99;
        edgeMaterials.forEach(m => { m.transparent = hiding; m.opacity = tFade; });
        vertexMaterials.forEach(m => { m.transparent = hiding; m.opacity = tFade; });
        faceMat.opacity = tFade * 0.08;
        tesseractGroup.visible = tFade > 0.02;
        net.orbitGroups.forEach(og => {
          og.pivot.visible = tFade > 0.05;
        });
        starfield.visible = tFade > 0.05;
        genesisPivot.visible = genesisRef.current && tFade > 0.05;
        searchedPivot.visible = !!searchedBlockRef.current && tFade > 0.05;

      }
      if (globeFadeRef.current > 0.01) {
        globeWireframe.rotation.y += delta * 0.05;
        landmassMesh.rotation.y = globeWireframe.rotation.y;
        coastlineLines.rotation.y = globeWireframe.rotation.y;
      }

      render();
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    const netState = netRef.current;

    return () => {
      canvasEl?.removeEventListener('mousemove', handleMouseMove);
      canvasEl?.removeEventListener('click', handleClick);
      canvasEl?.removeEventListener('pointerdown', handlePointerDownForClick);
      edgeMaterials.forEach(mat => mat.dispose());
      vertexMaterials.forEach(mat => mat.dispose());
      faceMeshes.forEach(f => f.geo.dispose());
      faceMat.dispose();
      vertexMeshes.forEach(m => { if (m.material) m.material.dispose(); });
      netState.cubeMats.forEach(m => m.dispose());
      netState.cubeGroups.forEach(cg => { if (cg.userData.labelMat) { cg.userData.labelMat.map?.dispose(); cg.userData.labelMat.dispose(); } });
      disposeChainCubes();
      btcLogo.dispose();
      ghostMats.forEach(m => m.dispose());
      starfield.dispose();
      sparkles.dispose();
      particleSwirl.dispose();
      genesisMat.dispose();
      genesisLabelMat.map?.dispose();
      genesisLabelMat.dispose();
      searchedMat.dispose();
      searchedLabelMat?.map?.dispose();
      searchedLabelMat?.dispose();
      pizzaGeos.forEach(g => g.dispose());
      pizzaMats.forEach(m => m.dispose());
      konamiGeo.dispose();
      konamiMat.dispose();
      lightningLines.forEach(passes => passes.forEach(l => l.geo.dispose()));
      lightningMaterial.dispose();
      lightningGlowMat.dispose();
      fireworkBursts.forEach(b => { b.geo.dispose(); b.mat.dispose(); });
      satPoints.geometry.dispose();
      satPoints.material.dispose();
      globeGeo.dispose();
      globeEdges.dispose();
      globeWireMat.dispose();
      coastlineGeo.dispose();
      coastlineMat.dispose();
      landmassGeo.dispose();
      landmassMat.dispose();
      landmassTex.dispose();
      disposeSharedGeo();
      dispose();
    };
  }, [initScene, updateTimer, getElapsed, getDelta, render, dispose, sceneRef, rendererRef, cameraRef, composerRef, animationRef, joystickRef, rotationJoystickRef]);

  return (
    <Box
      as="canvas"
      ref={canvasRef}
      position="absolute"
      top={0}
      left={0}
      w="100%"
      h="100%"
    />
  );
};
